import {
  AppFilter, AppTable, AppTableColumn, AssetsBlockedLists, Country,
  DspAccountSharedAsset, GroupAppFilter, WarningBoxConfig
} from '../../_models/models';
import { DateHelper } from '../../_common/date.helper';

const today: Date = DateHelper.getTodayInEasternTime().toDate();
const tomorrow: Date = DateHelper.getTomorrowInEasternTime().toDate();

export const auditEndpoints = {
  PUBLISHER: 'publisher/',
  DEAL: 'deal/',
  DEAL_PACKAGE: 'dealPackage/',
  DEAL_PARTNER: 'deal-partners/',
  TRAFFIC_SOURCE: 'trafficSource/'
};

export const auditHistoryWarningBox: WarningBoxConfig =
  new WarningBoxConfig('basic',
    null,
    'Changes from 08/09/2026 onward are shown here. For earlier changes, see the Action Log.');

export const auditTableButtons = [
  { id: 'auditResetFiltersButton', type: 'LINK', title: 'Reset Filters', hasLoadingSpinner: false },
];

export const publisherAuditTableConfig: AppTable = new AppTable('timestamp', 1, 10, 'timestamp',
  'desc', true, true);

export interface AuditFieldTranslationConfig {
  matchListName: string;
  matchProp: string;
  nameProp: string;
  normalizeKey?: (key: string) => string;
}

export const countryTranslationConfig: AuditFieldTranslationConfig = {
  matchListName: 'countries',
  matchProp: 'alpha2',
  nameProp: 'name',
  normalizeKey: (key: string) => key.toUpperCase()
};

export const publisherFieldsToUiMapping = {
  'bcat': { displayName: 'Blocked Categories', matchListName: 'iabCategoriesFlattened', matchProp: 'key', nameProp: 'title' },
  'btype': { displayName: 'Blocked Ad Types', matchListName: 'iabBannerAdTypes', matchProp: 'adTypeId', nameProp: 'description' },
  'battr': {
    displayName: 'Blocked Creative Attributes',
    matchListName: 'iabCreativeAttributes',
    matchProp: 'attributeId',
    nameProp: 'description'
  },
  'badv': { displayName: 'Blocked Advertisers' },
  'tldBadv': { displayName: 'Blocked Tld' },
  'bidfloorOpti': { displayName: 'Bid floor adjustment optimization' },
  'commissionOpti': { displayName: 'Commission optimization' },
  'dspAccountExcludeList': { displayName: 'Blocked Dsp', matchListName: 'dspAccountsFlattened', matchProp: 'id', nameProp: 'nameDisplay' },
  'dealPartnerExcludeList': {
    displayName: 'Blocked Curated Marketplace Partners',
    matchListName: 'dealPartners',
    matchProp: 'id',
    nameProp: 'name'
  },
  'cat': { displayName: 'Categories', matchListName: 'iabCategoriesFlattened', matchProp: 'key', nameProp: 'title' },
  ['publisher.floors.show']: { displayName: 'Show Floor Settings' },
  ['publisher.reporting.detailed']: { displayName: 'Detailed Reporting' },
  ['publisher.reporting.demand']: { displayName: 'Demand Reporting' },
  ['publisher.reporting.deals']: { displayName: 'Deal Reporting' },
  ['publisher.deal.show']: { displayName: 'Deals Section' },
  'country': { displayName: 'Country', ...countryTranslationConfig },
  'publisherRegionCountryFilters': { displayName: 'Blocked Countries by Region' },
  'tsFeePctOverride': { displayName: 'Traffic Source Fee Override' },
  'deactivationReason': { displayName: 'Deactivation Reason' },
  'statusModificationDate': { displayName: 'Status Modification Date' }
};

export const dealTypeValueToUiMapping: { [key: string]: string } = {
  open_auction: 'Non-Preferred - Single Buyer',
  open_auction_multi: 'Non-Preferred - Multi Buyer',
  open_audtion_multi: 'Non-Preferred - Multi Buyer',
  preferred: 'Preferred',
  programmatic_direct: 'Programmatic Guaranteed',
  programatic_direct: 'Programmatic Guaranteed'
};

export const auctionTypeValueToUiMapping: { [key: string]: string } = {
  first: 'Dynamic Price',
  fixed: 'Fixed Price'
};

export const buyerTypeValueToUiMapping: { [key: string]: string } = {
  standard: 'Non-Preferred',
  direct: 'Programmatic Guaranteed',
  'direct demand': 'Programmatic Guaranteed',
  preferred: 'Preferred'
};

export const applyPaDiscountValueToUiMapping: { [key: string]: string } = {
  true: 'PAD & Spend',
  false: 'Spend Only'
};

export const dealFieldNameToUiMapping: { [key: string]: string } = {
  description: 'Deal Name',
  notes: 'Description',
  configType: 'Deal Configuration',
  applyPaDiscount: 'Apply PAD Discount',
  bcatExclusions: 'Buyer\'s Category',
  dayParting: 'Day Parting',
  buyerType: 'Deal Type',
  revenueCap: 'Overall Deal Budget (USD)'
};

export const dealPackageFieldNameToUiMapping: { [key: string]: string } = {
  description: 'Inventory Package Name',
  packageConfigType: 'Inventory Package Type',
  placements: 'Linked Ad Placements',
  filterGenre: 'Content Genre',
  filterGenreList: 'Content Genre List',
  filterImpType: 'Impression Type',
  filterDeviceType: 'Device Types',
  filterSite: 'Media Type: Site',
  filterApp: 'Media Type: App',
  filterCoppa: 'COPPA supply only',
  filterCountryList: 'Countries',
  filterZipCode: 'Zip Codes',
  filterZipCodeList: 'Zip Codes List',
  filterRegion: 'Region/State',
  filterRegionList: 'Region/State List',
  filterMetro: 'Metro',
  filterMetroList: 'Metro List',
  filterViewabilityPercent: 'Viewability Score',
  filterCtrPercent: 'Click Through Rate',
  filterVcrPercent: 'Video Completion Rate'
};

export const trafficSourceFieldsToUiMapping = {
  'allowTmaxOverride': { displayName: 'Allow Max Timeout Override' },
  'defaultPlacementId': { displayName: 'Default Placement ID' },
  'displayName': { displayName: 'Display Name' },
  'tmax': { displayName: 'Max Timeout (Tmax)' },
  'rxSspName': { displayName: 'RX SSP Name' },
  'shortName': { displayName: 'Short Name' },
  'status': { displayName: 'Status' },
  'supportBvod': { displayName: 'Support BVOD' },
  'trafficSourceFeePct': { displayName: 'Traffic Source Fee' },
  'tsFeeReducesBid': { displayName: 'Traffic Source Fee Reduces Bid' }
};

const publisherSelectGroups: GroupAppFilter[] = [
  {
    groupId: 1, groupName: 'profileTab', groupDisplayName: 'Profile tab', possibleValues:
      [ { id: 1, displayName: 'Publisher Type', value: 'publisherAccountType' },
        { id: 2, displayName: 'Publisher Initiated Platform Fee', value: 'pubInitiatedDealPlatformfee' },
        { id: 3, displayName: 'Nexxen Initiated Platform Fee', value: 'unrulyInitiatedDealPlatformfee' },
        { id: 4, displayName: 'Accounting ID', value: 'accountingId' },
        { id: 5, displayName: 'Tax ID', value: 'taxId' },
        { id: 6, displayName: 'ZIP ID', value: 'zipId' },
        { id: 7, displayName: 'Internal Notes', value: 'internalNotes' },
        { id: 8, displayName: 'Company Name', value: 'company' },
        { id: 9, displayName: 'Deactivation Reason', value: 'deactivationReason' },
        { id: 10, displayName: 'Status Modification Date', value: 'statusModificationDate' }]
  }, {
    groupId: 2, groupName: 'configurationsTab', groupDisplayName: 'Configurations tab', possibleValues:
      [ { id: 1, displayName: 'Bid Floor', value: 'bidFloor' },
        { id: 2, displayName: 'Commission', value: 'commission' },
        { id: 3, displayName: 'Commission Type', value: 'commissionType' },
        { id: 4, displayName: 'Demand Profile', value: 'demandProfile' },
        { id: 5, displayName: 'Floor Adjustment', value: 'floorAdjustment' },
        { id: 6, displayName: 'QPS Defined', value: 'qpsDefined' },
        { id: 7, displayName: 'QPS Limit', value: 'qpsLimit' },
        { id: 8, displayName: 'Traffic Source Fee Override', value: 'tsFeePctOverride' }
      ]
    // {id: 6, displayName: 'Site/App Bundles', value: 'domainBundle'}]
  }, {
    groupId: 3, groupName: 'blockedListsTab', groupDisplayName: 'Blocked Lists tab', possibleValues:
      [ { id: 1, displayName: 'Blocked Categories', value: 'bcat' },
        { id: 2, displayName: 'Blocked Advertisers', value: 'badv' },
        { id: 3, displayName: 'Blocked DSPs', value: 'dspAccountExcludeList' },
        { id: 4, displayName: 'Blocked Curated Marketplace Partners', value: 'dealPartnerExcludeList' },
        { id: 5, displayName: 'Blocked Countries by Region', value: 'publisherRegionCountryFilters' }]
  },
];

const dealPartnerSelectGroups: GroupAppFilter[] = [
  {
    groupId: 1, groupName: 'internalInformation', groupDisplayName: 'Internal Information', possibleValues:
      [ { id: 1, displayName: 'Partner Name', value: 'name' },
        { id: 2, displayName: 'Status', value: 'status' },
        { id: 3, displayName: 'Platform Fee', value: 'platformFee' },
        { id: 4, displayName: 'Platform Fee Type', value: 'platformFeeType' },
        { id: 5, displayName: 'Default Demand Margin', value: 'defaultDemandMargin' }]
  }, {
    groupId: 2, groupName: 'audienceProviders', groupDisplayName: 'Audiences', possibleValues:
      [ { id: 1, displayName: 'Audience Provider', value: 'providerName' },
        { id: 2, displayName: 'Audience Provider ID', value: 'providerId' }]
  }];

const dealSelectGroups: GroupAppFilter[] = [
  {
    groupId: 1, groupName: 'detailsSection', groupDisplayName: 'Details', possibleValues:
      [ { id: 1, displayName: 'Deal Name', value: 'description' },
        { id: 2, displayName: 'Description', value: 'notes' },
        { id: 3, displayName: 'Status', value: 'status' },
        { id: 4, displayName: 'Date Range', value: 'dealStart' },
        { id: 5, displayName: 'External Deal ID', value: 'externalDealId' },
        { id: 6, displayName: 'Deal Configuration', value: 'configType' },
        { id: 7, displayName: 'Deal Type', value: 'dealType' },
        { id: 8, displayName: 'Auction Type', value: 'auctionType' },
        { id: 9, displayName: 'Currency', value: 'bidfloorCurrency' },
        { id: 10, displayName: 'Platform Fee Type', value: 'platformfeeType' },
        { id: 11, displayName: 'Platform Fee', value: 'customPlatformfee' },
        { id: 12, displayName: 'Platform Fee Type (Percent/Fixed)', value: 'customPlatformfeeType' },
        { id: 13, displayName: 'PAD Agreement', value: 'paId' }]
  }, {
    groupId: 2, groupName: 'buyersSection', groupDisplayName: 'Buyers', possibleValues:
      [ { id: 1, displayName: 'Buyer DSP', value: 'dspAccount' },
        { id: 2, displayName: 'Buyer Status', value: 'buyerStatus' },
        { id: 3, displayName: 'Buyer Bid Floor', value: 'defaultBidfloor' },
        { id: 4, displayName: 'Buyer Seat ID', value: 'wseat' },
        { id: 5, displayName: 'Buyer Adomain', value: 'wadomain' },
        { id: 6, displayName: 'Buyer\'s Category', value: 'bcatExclusions' },
        { id: 7, displayName: 'Marketplace Mode', value: 'marketplaceMode' },
        { id: 8, displayName: 'Marketplace Description', value: 'marketplaceDescription' }]
  }, {
    groupId: 3, groupName: 'volumeControlsSection', groupDisplayName: 'Volume Controls', possibleValues:
      [ { id: 1, displayName: 'Daily Request Limit', value: 'dailyReqCap' },
        { id: 2, displayName: 'Enable Request Pacing', value: 'paceReqs' },
        { id: 3, displayName: 'Daily Impression Limit', value: 'dailyImpCap' },
        { id: 4, displayName: 'Overall Impression Limit', value: 'overallImpCap' },
        { id: 5, displayName: 'Daily Impression Frequency Cap', value: 'frequencyCapDaily' },
        { id: 6, displayName: 'Monthly Impression Frequency Cap', value: 'frequencyCapMonthly' },
        { id: 7, displayName: 'Overall Deal Budget (USD)', value: 'revenueCap' }]
  }, {
    groupId: 4, groupName: 'otherSection', groupDisplayName: 'Other', possibleValues:
      [ { id: 1, displayName: 'Inventory Package', value: 'inventoryPackage' },
        { id: 2, displayName: 'Day Parting', value: 'dayParting' }]
  }];

export const CONTENT_TARGETING_FIELD_VALUE = 'Content Targeting';
export const CONTENT_TARGETING_FIELD_NAMES = ['publisherDealPackageKvpMaps', 'publisherDealPackageKvpFilterList', 'filterType'];

const inventoryPackageSelectGroups: GroupAppFilter[] = [
  {
    groupId: 1, groupName: 'detailsSection', groupDisplayName: 'Details', possibleValues:
      [ { id: 1, displayName: 'Inventory Package Name', value: 'description' },
        { id: 2, displayName: 'Inventory Package Type', value: 'packageConfigType' },
        { id: 3, displayName: 'Publisher', value: 'publisher' },
        { id: 4, displayName: 'Linked Ad Placements', value: 'placements' },
        { id: 5, displayName: 'Include Future Placements', value: 'includeFuturePlacements' },
        { id: 6, displayName: 'Description', value: 'notes' }]
  }, {
    groupId: 2, groupName: 'inventorySection', groupDisplayName: 'Inventory', possibleValues:
      [ { id: 1, displayName: 'Device Types', value: 'filterDeviceType' },
        { id: 2, displayName: 'Impression Type', value: 'filterImpType' },
        { id: 3, displayName: 'Media Type: Site', value: 'filterSite' },
        { id: 4, displayName: 'Media Type: App', value: 'filterApp' },
        { id: 5, displayName: 'COPPA supply only', value: 'filterCoppa' }]
  }, {
    groupId: 3, groupName: 'geoSection', groupDisplayName: 'Geo', possibleValues:
      [ { id: 1, displayName: 'Country Filter Type', value: 'filterCountry' },
        { id: 2, displayName: 'Countries', value: 'countries' },
        { id: 3, displayName: 'Region Filter Type', value: 'filterRegion' },
        { id: 4, displayName: 'Regions', value: 'regions' },
        { id: 5, displayName: 'DMA Filter Type', value: 'filterDma' },
        { id: 6, displayName: 'DMAs', value: 'metros' },
        { id: 7, displayName: 'Zip Code Filter Type', value: 'filterZipCode' },
        { id: 8, displayName: 'Zip Codes', value: 'zipCodes' }]
  }, {
    groupId: 4, groupName: 'contentTargetingSection', groupDisplayName: 'Content', possibleValues:
      [ { id: 1, displayName: 'Content Genre', value: 'filterGenre' },
        { id: 2, displayName: 'Content Genre List', value: 'filterGenreList' },
        { id: 3, displayName: 'Content Language Filter Type', value: 'filterContentLanguage' },
        { id: 4, displayName: 'Content Language', value: 'filterContentLanguageList' },
        { id: 5, displayName: 'Content Rating Filter Type', value: 'filterContentRating' },
        { id: 6, displayName: 'Content Rating', value: 'filterContentRatingList' },
        { id: 7, displayName: 'Live Stream', value: 'filterContentLivestream' },
        { id: 8, displayName: 'Content Targeting', value: CONTENT_TARGETING_FIELD_VALUE }]
  }, {
    groupId: 5, groupName: 'audienceTargetingSection', groupDisplayName: 'Audience Targeting', possibleValues:
      [ { id: 1, displayName: 'Audience Segments', value: 'segmentGroups' }]
  }, {
    groupId: 6, groupName: 'siteAppBundlesSection', groupDisplayName: 'Site/App Bundles', possibleValues:
      [ { id: 1, displayName: 'Site/App Bundles Filter Type', value: 'filterDomainBundle' },
        { id: 2, displayName: 'Site/App Bundles', value: 'domainBundles' }]
  }, {
    groupId: 7, groupName: 'qualityPerformanceSection', groupDisplayName: 'Quality & Performance', possibleValues:
      [ { id: 1, displayName: 'Viewability Score', value: 'filterViewabilityPercent' },
        { id: 2, displayName: 'Video Completion Rate (VCR)', value: 'filterVcrPercent' },
        { id: 3, displayName: 'Click Through Rate (CTR)', value: 'filterCtrPercent' }]
  }];

const trafficSourceSelectGroups: GroupAppFilter[] = [
  {
    groupId: 1, groupName: 'trafficSourceFields', groupDisplayName: 'Traffic Source Fields', possibleValues:
      [ { id: 1, displayName: 'Allow Max Timeout Override', value: 'allowTmaxOverride' },
        { id: 2, displayName: 'Default Placement ID', value: 'defaultPlacementId' },
        { id: 3, displayName: 'Display Name', value: 'displayName' },
        { id: 4, displayName: 'Max Timeout (Tmax)', value: 'tmax' },
        { id: 5, displayName: 'RX SSP Name', value: 'rxSspName' },
        { id: 6, displayName: 'Short Name', value: 'shortName' },
        { id: 7, displayName: 'Status', value: 'status' },
        { id: 8, displayName: 'Support BVOD', value: 'supportBvod' },
        { id: 9, displayName: 'Traffic Source Fee', value: 'trafficSourceFeePct' },
        { id: 10, displayName: 'Traffic Source Fee Reduces Bid', value: 'tsFeeReducesBid' }]
  }];




const createAuditTableColumns = (prefix: string, includeParent: boolean): AppTableColumn[] => [
  { id: `${prefix}AuditTimestamp`, prop: 'timestamp', displayName: 'Timestamp', type: 'text', isSortEnabled: true },
  { id: `${prefix}AuditUsername`, prop: 'username', displayName: 'User Name', type: 'text', isSortEnabled: false },
  { id: `${prefix}AuditField`, prop: 'fieldName', displayName: 'Field Name', type: 'text', isSortEnabled: false },
  ...(includeParent ? [{ id: `${prefix}AuditParent`, prop: 'parent', displayName: 'Parent', type: 'text', isSortEnabled: false }] : []),
  { id: `${prefix}AuditActionType`, prop: 'actionType', displayName: 'Action Type', type: 'text', isSortEnabled: false },
  { id: `${prefix}AuditPreviousValue`, prop: 'previousValue', displayName: 'Previous Value', type: 'text', isSortEnabled: false },
  { id: `${prefix}AuditNewValue`, prop: 'newValue', displayName: 'New Value', type: 'text', isSortEnabled: false },
];

export const publisherAuditTableColumns: AppTableColumn[] = createAuditTableColumns('publisher', true);
export const trafficSourceAuditTableColumns: AppTableColumn[] = createAuditTableColumns('trafficSource', false);

// Create separate filter configurations for different audit types
const dealPartnerFilters: AppFilter[] = [
  new AppFilter('auditDatesRange', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '',
    'default', true, false, null, [],
    [today, tomorrow], null, null, null, true,
    [today, tomorrow], null, null,
    null, null, true),
  new AppFilter('auditDealPartnerFieldName', 'SELECT_WITH_GROUPS', 'field', 'Field Name', 'Select field name',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, dealPartnerSelectGroups),
  new AppFilter('auditPlatformFee', 'SELECT', 'platformFee', 'Platform Fee', 'Select platform fee',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Demand Margin', value: 'demandMargin' },
      { id: 2, displayName: 'Cost', value: 'cost' }],
    []),
  new AppFilter('auditPartnerUser', 'SELECT', 'userId', 'Partner User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditInternalUser', 'SELECT', 'userId', 'Internal User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditActionType', 'SELECT', 'action', 'Action Type', 'Select action type',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Create', value: 'INITIAL' },
      { id: 2, displayName: 'Update', value: 'UPDATE' }],
    [])
];

const defaultFilters: AppFilter[] = [
  new AppFilter('auditDatesRange', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '',
    'default', true, false, null, [],
    [today, tomorrow], null, null, null, true,
    [today, tomorrow], null, null,
    null, null, true),
  new AppFilter('auditFieldName', 'SELECT_WITH_GROUPS', 'field', 'Field Name', 'Select field name',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, publisherSelectGroups),
  new AppFilter('auditPublisherUser', 'SELECT', 'userId', 'Publisher User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditInternalUser', 'SELECT', 'userId', 'Internal User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditActionType', 'SELECT', 'action', 'Action Type', 'Select action type',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Create', value: 'INITIAL' },
      { id: 2, displayName: 'Update', value: 'UPDATE' }],
    [])
];

const trafficSourceFilters: AppFilter[] = [
  new AppFilter('auditDatesRange', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '',
    'default', true, false, null, [],
    [today, today], null, null, null, true,
    [today, today], null, null,
    null, null, true),
  new AppFilter('auditTrafficSourceFieldName', 'SELECT_WITH_GROUPS', 'field', 'Field Name', 'Select field name',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, trafficSourceSelectGroups),
  new AppFilter('auditInternalUser', 'SELECT', 'userId', 'Internal User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditActionType', 'SELECT', 'action', 'Action Type', 'Select action type',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Create', value: 'INITIAL' },
      { id: 2, displayName: 'Update', value: 'UPDATE' }],
    [])
];

const dealFilters: AppFilter[] = [
  new AppFilter('auditDatesRange', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '',
    'default', true, false, null, [],
    [today, today], null, null, null, true,
    [today, today], null, null,
    null, null, true),
  new AppFilter('auditDealFieldName', 'SELECT_WITH_GROUPS', 'field', 'Field Name', 'Select field name',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, dealSelectGroups),
  new AppFilter('auditPublisherUser', 'SELECT', 'userId', 'Publisher User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditInternalUser', 'SELECT', 'userId', 'Internal User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditActionType', 'SELECT', 'action', 'Action Type', 'Select action type',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Create', value: 'INITIAL' },
      { id: 2, displayName: 'Update', value: 'UPDATE' }],
    [])
];

const inventoryPackageFilters: AppFilter[] = [
  new AppFilter('auditDatesRange', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '',
    'default', true, false, null, [],
    [today, today], null, null, null, true,
    [today, today], null, null,
    null, null, true),
  new AppFilter('auditInventoryPackageFieldName', 'SELECT_WITH_GROUPS', 'field', 'Field Name', 'Select field name',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, inventoryPackageSelectGroups),
  new AppFilter('auditPublisherUser', 'SELECT', 'userId', 'Publisher User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditInternalUser', 'SELECT', 'userId', 'Internal User', 'Select user',
    'default', true, false, null,
    [],
    [], null,
    null, null, null, []),
  new AppFilter('auditActionType', 'SELECT', 'action', 'Action Type', 'Select action type',
    'default', true, false, null,
    [
      { id: 1, displayName: 'Create', value: 'INITIAL' },
      { id: 2, displayName: 'Update', value: 'UPDATE' }],
    [])
];

export const getAuditFiltersConfig = (auditType: string): AppFilter[] => {
  switch (auditType) {
    case 'DEAL':
      return dealFilters;
    case 'DEAL_PACKAGE':
      return inventoryPackageFilters;
    case 'DEAL_PARTNER':
      return dealPartnerFilters;
    case 'TRAFFIC_SOURCE':
      return trafficSourceFilters;
    default:
      return defaultFilters;
  }
};

// Replace the existing auditFiltersConfig export with the function
export const auditFiltersConfig = defaultFilters;

export const AUDIT_MOCK_NEW = {
  paginated: true,
  pageNumber: 1,
  pageSize: 10,
  totalElements: 1,
  data: [
    {
      timestamp: 234234234,
      username: 'Bob Sfog',
      field: 'publisherName',
      parent: 'Profile',
      previousValue: 'old name',
      newValue: 'new value',
      action: 'Update'
    }
  ],
};

export interface AuditTranslatableEntities extends AssetsBlockedLists {
  countries?: Country[];
  iabCategoriesFlattened?: Array<{ key: string; title: string }>;
  dspAccountsFlattened?: DspAccountSharedAsset[];
}

export const emptyAuditTranslatableEntitiesObject: AuditTranslatableEntities = {
  countries: [],
  iabCategories: [],
  iabCategoriesFlattened: [],
  iabBannerAdTypes: [],
  iabCreativeAttributes: [],
  dspAccounts: { activeDspAccountList: [], inactiveDspAccountList: [] },
  dspAccountsFlattened: [],
  dealPartners: [],
};
